import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Integration, IntegrationType } from '@pdqdev/pdq-shared/dist/entities/Integrations';
import { HttpStatusCode } from '@pdqdev/pdq-shared/dist/utils/http-status-codes';
import { Observable, of, Subject, throwError } from 'rxjs';
import { finalize, first, map, switchMap, takeUntil } from 'rxjs/operators';
import { IntegrationsService } from '@services/integrations/integrations.service';
import { ToastService } from '@storybook/services/toast/toast.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '@app/sharedModule/components/confirmation-dialog/confirmation-dialog.component';
import {
  ApiCredentialDialogComponent,
  ApiCredentialDialogConfig,
} from './api-credential-dialog/api-credential-dialog.component';
import { OrderProtectionConnectDialogComponent } from './order-protection-connect-dialog/order-protection-connect-dialog.component';
enum ToastMessages {
  FAILED_TO_CONNECT = 'Failed to connect with',
  FAILED_TO_DISCONNECT = 'Failed to disconnect from',
}

/**
 * Set on the deep-link Klaviyo uses as our marketplace Install URL: a merchant who clicks "Install" on the
 * Klaviyo listing lands here and the OAuth consent flow must kick off automatically (Klaviyo app-review
 * requirement — the Install URL must start auth, not drop the user on the dashboard).
 */
const AUTO_CONNECT_QUERY_PARAM = 'autoConnect';
/** The Install URL sets exactly `autoConnect=1`; ignore any other value so a stray/edited param can't kick off OAuth. */
const AUTO_CONNECT_ENABLED_VALUE = '1';
type TabConfig = {
  label: string;
  active: boolean;
  value: IntegrationTab;
};

const Alloy = (window as any).Alloy;

export interface AlloyModalConfig {
  platform?: string;
  alwaysShowAuth?: boolean;
  hide?: boolean;
  title: string;
}
enum IntegrationTypeName {
  TRIPLE_WHALE = 'triple_whale',
  RICHPANEL = 'richpanel',
  OKENDO = 'okendo',
  YOTPO = 'yotpo',
  JUDGE_ME = 'judge_me',
  BAZAARVOICE = 'bazaarvoice',
  ORDER_PROTECTION = 'order_protection',
}

enum IntegrationTab {
  ALL_RECIPES = 0,
  RECIPE_TEMPLATES = 1,
  OVERVIEW = 2,
  SETTINGS = 3,
  METRICS = 4,
}

const TabToDisplayName = new Map<IntegrationTab, string>([
  [IntegrationTab.ALL_RECIPES, 'Your recipes'],
  [IntegrationTab.RECIPE_TEMPLATES, 'Recipe templates'],
  [IntegrationTab.OVERVIEW, 'Overview'],
  [IntegrationTab.SETTINGS, 'Settings'],
  [IntegrationTab.METRICS, 'Metrics'],
]);

const YOTPO_CREDENTIAL_DIALOG_CONFIG: ApiCredentialDialogConfig = {
  integrationDisplayName: 'Yotpo',
  fieldLabel: 'App Key',
  fieldName: 'yotpoAppKey',
  helperText: 'Find your App Key in Yotpo: Profile Icon > Settings > General Settings > API Credentials',
  validatePath: 'integrations/yotpo/validate-app-key',
};

const OKENDO_CREDENTIAL_DIALOG_CONFIG: ApiCredentialDialogConfig = {
  integrationDisplayName: 'Okendo',
  fieldLabel: 'User ID',
  fieldName: 'okendoUserId',
  helperText: 'Find your User ID in Okendo: Settings > Integrations > API',
  validatePath: 'integrations/okendo/validate-user-id',
};

const JUDGE_ME_CREDENTIAL_DIALOG_CONFIG: ApiCredentialDialogConfig = {
  integrationDisplayName: 'Judge.me',
  fieldLabel: 'Private API Token',
  fieldName: 'judgeMeApiToken',
  helperText: 'Find your private API Token in Judge.me: Settings > Integrations > View API Token (top right)',
  validatePath: 'integrations/judge-me/validate-token',
};

const BAZAARVOICE_CREDENTIAL_DIALOG_CONFIG: ApiCredentialDialogConfig = {
  integrationDisplayName: 'Bazaarvoice',
  fieldLabel: 'API Passkey',
  fieldName: 'bazaarvoicePasskey',
  helperText: 'Find your Passkey in the Bazaarvoice Portal: Settings > API (under Developer) > API Keys',
  validatePath: 'integrations/bazaarvoice/validate-passkey',
};

@Component({
  selector: 'integration-details',
  templateUrl: './integration-details.component.html',
  styleUrls: ['./integration-details.component.scss'],
  standalone: false,
})
export class IntegrationDetailsComponent implements OnInit, OnDestroy {
  title;
  currentTab = IntegrationTab.OVERVIEW;
  tabs: TabConfig[] = [
    { value: IntegrationTab.OVERVIEW, label: TabToDisplayName.get(IntegrationTab.OVERVIEW), active: false },
  ];
  Tab = IntegrationTab;
  showSkeleton = true;
  integration: Integration;
  integrationType: IntegrationType;
  connectionLoading = false;
  logoUrl: string;
  destroy$ = new Subject();
  confirmationDialogRef: MatDialogRef<ConfirmationDialogComponent>;
  constructor(
    private integrationsService: IntegrationsService,
    private router: Router,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private matDialog: MatDialog,
  ) {}
  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit(): void {
    this.setupAlloyErrorHandler();

    this.initIntegration()
      .pipe(first(() => !!this.integrationType))
      .subscribe(() => {
        if (this.isApiKeyIntegration() && !this.integration?.isConnected) {
          this.currentTab = IntegrationTab.SETTINGS;
        }
        this.initTabs();
        this.maybeAutoConnect();
      });
  }

  /**
   * When a merchant arrives via Klaviyo's Install URL (?autoConnect=1), start the direct OAuth flow
   * immediately instead of waiting for a Connect click. Only applies to a `directConnect` type that isn't
   * already connected; the param is stripped first so a back-nav or a failed kickoff can't loop the flow.
   */
  private maybeAutoConnect(): void {
    if (this.route.snapshot.queryParamMap.get(AUTO_CONNECT_QUERY_PARAM) !== AUTO_CONNECT_ENABLED_VALUE) {
      return;
    }
    this.clearAutoConnectParam();
    const isDirectType = !!this.integrationType?.metadata?.directConnect;
    if (isDirectType && !this.integration?.isConnected) {
      this.startDirectConnect();
    }
  }

  private clearAutoConnectParam(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [AUTO_CONNECT_QUERY_PARAM]: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private setupAlloyErrorHandler(): void {
    const errorHandler = (event: ErrorEvent) => {
      const isAlloyPostMessageError =
        event.message?.includes('DataCloneError') &&
        (event.message?.includes('postMessage') || event.message?.includes('closeForgeModal'));

      if (isAlloyPostMessageError) {
        console.warn('[Alloy SDK] Non-critical postMessage error suppressed:', event.message);
        event.preventDefault();
        return true;
      }
      return false;
    };

    window.addEventListener('error', errorHandler);

    // Clean up on destroy
    this.destroy$.subscribe(() => {
      window.removeEventListener('error', errorHandler);
    });
  }
  backToIntegrations() {
    this.router.navigate(['..'], { relativeTo: this.route });
  }
  onTabChange(tab: IntegrationTab) {
    this.currentTab = tab;
    this.initTabs();
  }
  initIntegration() {
    return new Observable((subscriber) => {
      const integrationTypeName = this.route.snapshot.paramMap.get('name');
      this.integrationsService.integrations$.pipe(takeUntil(this.destroy$)).subscribe((integrations) => {
        this.integration = integrations?.[integrationTypeName]?.instance;
        this.integrationType = integrations?.[integrationTypeName]?.type;
        this.logoUrl = IntegrationsService.getMiniLogoUrl(integrationTypeName);
        this.initCurrentTabAfterConnectionChange();
        this.initSkeleton();
        this.initTabs();
        subscriber.next(integrations);
      });
    });
  }
  initSkeleton() {
    this.showSkeleton = !this.integrationType;
  }
  private initTabs() {
    this.tabs = [this.createTab(IntegrationTab.OVERVIEW)];
    if (this.integrationType?.recipeTemplates?.length) {
      this.tabs.unshift(this.createTab(IntegrationTab.RECIPE_TEMPLATES));

      if (this.integration?.isConnected) {
        this.tabs.unshift(this.createTab(IntegrationTab.ALL_RECIPES));
      }
    }

    if (IntegrationsService.hasSettings(this.integrationType?.name)) {
      this.tabs.push(this.createTab(IntegrationTab.SETTINGS));
    }

    if (this.integrationType?.name === IntegrationTypeName.TRIPLE_WHALE && this.integration?.isConnected) {
      this.tabs.unshift(this.createTab(IntegrationTab.METRICS));
    }
  }

  private initCurrentTabAfterConnectionChange() {
    if (!this.integration?.isConnected && this.currentTab === IntegrationTab.ALL_RECIPES) {
      this.currentTab = IntegrationTab.RECIPE_TEMPLATES;
    }
    if (!this.integration?.isConnected && this.currentTab === IntegrationTab.METRICS) {
      this.currentTab = IntegrationTab.OVERVIEW;
    }
  }

  private createTab(tab: IntegrationTab) {
    return {
      value: tab,
      label: TabToDisplayName.get(tab),
      active: this.currentTab === tab,
    };
  }

  private handleAuth(integration: Integration): Observable<any> {
    if (IntegrationsService.isAlloy(integration.type)) {
      return this.handleAlloyAuth();
    }
    if (integration.type.name === IntegrationTypeName.RICHPANEL) {
      return this.handleRichpanelAuth(integration);
    }
    if (integration.type.name === IntegrationTypeName.TRIPLE_WHALE) {
      return of();
    }
    if (integration.type.name === IntegrationTypeName.OKENDO) {
      return this.handleOkendoAuth();
    }
    if (integration.type.name === IntegrationTypeName.YOTPO) {
      return this.handleYotpoAuth();
    }
    if (integration.type.name === IntegrationTypeName.JUDGE_ME) {
      return this.handleJudgeMeAuth();
    }
    if (integration.type.name === IntegrationTypeName.BAZAARVOICE) {
      return this.handleBazaarvoiceAuth();
    }
    return of();
  }

  private handleYotpoAuth(): Observable<{ yotpoAppKey: string }> {
    return this.openCredentialDialog(YOTPO_CREDENTIAL_DIALOG_CONFIG).pipe(map((yotpoAppKey) => ({ yotpoAppKey })));
  }

  private handleJudgeMeAuth(): Observable<{ judgeMeApiToken: string }> {
    return this.openCredentialDialog(JUDGE_ME_CREDENTIAL_DIALOG_CONFIG).pipe(
      map((judgeMeApiToken) => ({ judgeMeApiToken })),
    );
  }

  private handleBazaarvoiceAuth(): Observable<{ bazaarvoicePasskey: string }> {
    return this.openCredentialDialog(BAZAARVOICE_CREDENTIAL_DIALOG_CONFIG).pipe(
      map((bazaarvoicePasskey) => ({ bazaarvoicePasskey })),
    );
  }

  private handleOkendoAuth(): Observable<{ okendoUserId: string }> {
    return this.openCredentialDialog(OKENDO_CREDENTIAL_DIALOG_CONFIG).pipe(map((okendoUserId) => ({ okendoUserId })));
  }

  private openCredentialDialog(config: ApiCredentialDialogConfig): Observable<string> {
    const dialogRef = this.matDialog.open<ApiCredentialDialogComponent, ApiCredentialDialogConfig, string | null>(
      ApiCredentialDialogComponent,
      { data: config },
    );
    return dialogRef
      .afterClosed()
      .pipe(switchMap((value) => (value ? of(value) : throwError(() => new Error('Credential dialog cancelled')))));
  }

  private handleAlloyAuth() {
    const modalOptions = this.createAlloyModalOptions();

    return this.integrationsService.getAlloyUserToken().pipe(
      switchMap(
        ({ token }: { token: string }) =>
          new Observable((observer) => {
            Alloy.setToken(token);

            const handleCallback = (result: { success: boolean }) => {
              if (result.success) {
                observer.next(undefined);
                observer.complete();
              } else {
                observer.error(new Error('Alloy authentication failed'));
              }
            };

            Alloy.install({
              ...modalOptions,
              callback: handleCallback,
            });
          }),
      ),
    );
  }

  private handleRichpanelAuth(integration: Integration) {
    return integration?.metadata?.apikey?.length
      ? of(integration.metadata.apikey)
      : throwError(() => new Error('API key is missing in integration metadata'));
  }

  private createAlloyModalOptions(): AlloyModalConfig {
    const alloyIntegrationId = this.integrationType.metadata?.alloyV2?.integrationId;
    const workflowIds = this.integrationType.metadata?.alloyV2?.workflowIds;

    const modalOptions: any = {
      alwaysShowAuthentication: true,
      hide: false,
      title: `Connect with ${this.integrationType.displayName}`,
    };
    if (alloyIntegrationId) {
      modalOptions.integrationId = alloyIntegrationId;
    } else if (workflowIds) {
      modalOptions.workflowIds = workflowIds;
    }

    return modalOptions;
  }
  private openExternalLink(link: string) {
    window.open(link || '', '_blank');
  }

  async handleDisconnect() {
    this.confirmationDialogRef = this.matDialog.open(ConfirmationDialogComponent, {
      data: {
        title: `Disconnect with ${this.integrationType.displayName} ?`,
        description: `You are about to disconnect from ${this.integrationType.displayName}.`,
      },
    });

    this.confirmationDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.handleConnect();
      }
    });
  }

  async handleConnect() {
    const newIsConnected = !this.integration?.isConnected;

    // A type flagged `directConnect` (e.g. Klaviyo) always connects via the direct
    // OAuth flow — never the Alloy modal. Disconnect tears down whatever transport is live: the direct
    // token when the shop is on direct, otherwise the generic (Alloy) teardown further below.
    // Order Protection has a bespoke two-step connect (request collaboration → merchant approves → finalize)
    // handled entirely inside its own dialog; disconnect keeps the generic teardown below.
    if (this.integrationType?.name === IntegrationTypeName.ORDER_PROTECTION && newIsConnected) {
      this.openOrderProtectionConnectDialog();
      return;
    }

    const isDirectType = !!this.integrationType?.metadata?.directConnect;
    if (isDirectType && newIsConnected) {
      this.startDirectConnect();
      return;
    }
    // a shop already on direct must disconnect through the direct route, even if we stopped offering it
    if (!newIsConnected && this.isOnDirectTransport()) {
      this.disconnectDirect();
      return;
    }

    const externalInstall = this.integrationType?.metadata?.externalInstall;
    if (newIsConnected && externalInstall) {
      this.openExternalLink(this.integrationType.metadata.installConfig.appExternalUrl);
      return;
    }
    this.connectionLoading = true;
    /** First connection step, execute only when this is a first connection for the shop with this platform*/
    const integrationInstance = !this.integration
      ? this.integrationsService.createIntegration(this.integrationType.name)
      : of(this.integration);

    /** Second connection step, ask user for platform specific credentials or configuration
     then report it through the integrationService.installConnection*/
    integrationInstance
      .pipe(
        switchMap(
          (integration) =>
            new Observable((subscriber) =>
              (newIsConnected ? this.handleAuth(integration) : of(null)).subscribe(
                (config) => subscriber.next({ integration, config }),
                (err) => subscriber.error(err),
              ),
            ),
        ),
        switchMap(({ integration, config }: { integration: Integration; config: any }) => {
          if (!newIsConnected && externalInstall) {
            this.openExternalLink(this.integrationType.metadata.installConfig.appExternalUrl);
          }
          return this.integrationsService.toggleConnection(integration, newIsConnected, config);
        }),
      )
      .subscribe(
        (updatedIntegration: Integration) => {
          this.connectionLoading = false;
        },
        (err) => {
          this.handleConnectFailure(err, newIsConnected);
          this.connectionLoading = false;
        },
      );
  }

  private static readonly API_KEY_INTEGRATIONS: string[] = [IntegrationTypeName.TRIPLE_WHALE];

  isApiKeyIntegration(): boolean {
    return IntegrationDetailsComponent.API_KEY_INTEGRATIONS.includes(this.integrationType?.name);
  }

  /** True when this integration is currently on the direct (OAuth) transport. */
  private isOnDirectTransport(): boolean {
    return this.integration?.metadata?.transport === 'direct';
  }

  /** Open the Order Protection connect dialog; reconcile integration state once it closes. */
  private openOrderProtectionConnectDialog(): void {
    this.matDialog
      .open(OrderProtectionConnectDialogComponent)
      .afterClosed()
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.integrationsService.refreshIntegrations()),
      )
      .subscribe();
  }

  // the single dispatch point — a new direct integration is a provider entry in core, not a change here
  private startDirectConnect(): void {
    if (this.integrationType.metadata?.directAuthMethod === 'apiKey') {
      this.connectDirectWithApiKey();
      return;
    }
    this.connectDirect();
  }

  private connectDirect(): void {
    this.connectionLoading = true;
    this.integrationsService
      .getDirectOAuthUrl(this.integrationType.metadata?.directSlug ?? this.integrationType.name)
      .pipe(first())
      .subscribe(
        ({ url }) => window.open(url, '_self'),
        (err) => {
          this.connectionLoading = false;
          this.toastService.showToast({
            mode: 'danger',
            message:
              err?.status === HttpStatusCode.FORBIDDEN
                ? `A direct ${this.integrationType.displayName} connection is not enabled for your store yet`
                : `${ToastMessages.FAILED_TO_CONNECT} ${this.integrationType.displayName}`,
          });
        },
      );
  }

  private connectDirectWithApiKey(): void {
    const metadata = this.integrationType.metadata;
    this.openCredentialDialog({
      integrationDisplayName: this.integrationType.displayName,
      fieldLabel: metadata.apiKeyDialog.fieldLabel,
      // the route's body contract, not per-vendor config
      fieldName: 'apiKey',
      helperText: metadata.apiKeyDialog.helperText,
      validatePath: `integrations/direct/${metadata.directSlug}/connect`,
    })
      .pipe(
        first(),
        switchMap(() => this.integrationsService.refreshIntegrations()),
      )
      .subscribe({
        next: () =>
          this.toastService.showToast({
            mode: 'success',
            message: `Connected to ${this.integrationType.displayName}`,
          }),
        // a cancelled dialog rejects, and a rejected key is already shown inside the dialog
        error: () => undefined,
      });
  }

  /** Tear down the live direct transport (revoke token) instead of the generic connection flip. */
  private disconnectDirect(): void {
    this.connectionLoading = true;
    this.integrationsService
      .disconnectDirect(this.integrationType.metadata?.directSlug ?? this.integrationType.name)
      .pipe(
        first(),
        finalize(() => (this.connectionLoading = false)),
      )
      .subscribe(
        () => {
          this.toastService.showToast({
            mode: 'success',
            message: `Disconnected from ${this.integrationType.displayName}`,
          });
        },
        () => {
          this.toastService.showToast({
            mode: 'danger',
            message: `${ToastMessages.FAILED_TO_DISCONNECT} ${this.integrationType.displayName}`,
          });
        },
      );
  }

  private handleConnectFailure(err, newIsConnected: boolean) {
    this.toastService.showToast({
      mode: 'danger',
      message: `${newIsConnected ? ToastMessages.FAILED_TO_CONNECT : ToastMessages.FAILED_TO_DISCONNECT} ${
        this.integrationType.displayName
      }`,
    });
  }
}
